import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import React from 'react';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  /** Renders a smaller inline fallback instead of a full-screen takeover.
   * Use for boundaries nested inside an already-mounted shell. */
  compact?: boolean;
};

type ErrorBoundaryState = {
  error: Error | null;
};

function ErrorDetails({ error }: { error: Error }) {
  // eslint-disable-next-line no-undef
  if (process.env.NODE_ENV === 'production') return null;
  return (
    <pre
      style={{
        fontSize: 11,
        color: 'var(--text-faint)',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 10,
        maxHeight: 160,
        overflow: 'auto',
        textAlign: 'left',
        whiteSpace: 'pre-wrap'
      }}
    >
      {error.message}
    </pre>
  );
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error', error, info.componentStack);
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack }
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    if (this.props.compact) {
      return (
        <div className="panel" style={{ padding: 24, display: 'grid', gap: 12, justifyItems: 'start' }}>
          <div className="panelKicker" style={{ color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            This section hit an error
          </div>
          <p style={{ margin: 0, color: 'var(--text-soft)', fontSize: 13.5 }}>
            Something went wrong rendering this page. The rest of the app is unaffected — try
            reloading, or switch to another page from the sidebar.
          </p>
          <ErrorDetails error={error} />
          <button type="button" className="secondaryButton" onClick={this.handleReload}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Reload
          </button>
        </div>
      );
    }

    return (
      <div className="authShell">
        <div className="authCard">
          <div className="authKicker" style={{ color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            Something went wrong
          </div>
          <h1>This page hit an error</h1>
          <p>
            The interface ran into a problem it couldn&apos;t recover from. Your data is safe — try
            reloading the page. If this keeps happening, please let us know.
          </p>
          <ErrorDetails error={error} />
          <button type="button" className="primaryButton authSubmit" onClick={this.handleReload}>
            <RefreshCw size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export { ErrorBoundary };
